import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignIn, useCheckLogin } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { loginMap } from '@/constants/snsLoginMap';
import { handleClickLogin } from '@/utils/oauth';
import { SHA512 } from 'crypto-js';

const Login = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const signInMutation = useSignIn();
  const { data: userData, isLoading: isCheckingLogin } = useCheckLogin();

  useEffect(() => {
    if (!isCheckingLogin && userData) {
      const redirectUrl = sessionStorage.getItem('redirectUrl');
      if (redirectUrl && !redirectUrl.includes('/user/login')) {
        sessionStorage.removeItem('redirectUrl');
        window.location.href = redirectUrl;
        return;
      }

      sessionStorage.removeItem('redirectUrl');
      navigate('/user/mypage', { replace: true });
    }
  }, [userData, isCheckingLogin, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      const result = await signInMutation.mutateAsync({
        email,
        password: SHA512(password).toString(),
      });

      queryClient.invalidateQueries({ queryKey: ['LOGIN_CHECK'] });

      const redirectUrl = sessionStorage.getItem('redirectUrl');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectUrl');
        window.location.href = redirectUrl;
      } else {
        window.location.href = '/user/mypage';
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
    }
  };

  const handleSocialLogin = (sns: string) => {
    handleClickLogin(sns);
  };

  const socialLoginButtons = loginMap.default;

  if (isCheckingLogin) {
    return (
      <div className='min-h-screen bg-white flex items-center justify-center'>
        <p className='text-gray-600'>Checking authentication...</p>
      </div>
    );
  }

  if (userData) {
    return (
      <div className='min-h-screen bg-white flex items-center justify-center'>
        <p className='text-gray-600'>Redirecting...</p>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-white flex flex-col'>
      <div className='flex h-full w-full items-center p-[20px] gap-[8px]'>
        <p className='flex-1 text-center font-semibold text-black text-sm'>
          Login
        </p>
      </div>

      <div className='flex flex-col items-center justify-start flex-1 px-6 pt-8'>
        <form
          onSubmit={handleSubmit}
          className='w-full max-w-[400px] flex flex-col gap-6'
        >
          <div className='flex flex-col gap-2'>
            <label
              htmlFor='email'
              className='text-gray-800 text-base font-medium'
            >
              Email
            </label>
            <input
              type='email'
              id='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='Email'
              className='w-full h-12 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#fccb16] focus:ring-1 focus:ring-[#fccb16]'
              required
            />
          </div>

          <div className='flex flex-col gap-2'>
            <label
              htmlFor='password'
              className='text-gray-800 text-base font-medium'
            >
              Password
            </label>
            <input
              type='password'
              id='password'
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder='Password'
              className='w-full h-12 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#fccb16] focus:ring-1 focus:ring-[#fccb16]'
              required
            />
          </div>

          {error && <div className='text-red-600 text-sm'>{error}</div>}

          <button
            type='submit'
            disabled={signInMutation.isPending}
            className='w-full h-12 bg-[#fccb16] text-black rounded-lg font-medium text-base cursor-pointer transition-all hover:bg-[#ffd84d] disabled:bg-slate-400 disabled:cursor-not-allowed'
          >
            {signInMutation.isPending ? 'Logging in...' : 'Login'}
          </button>

          <div className='flex items-center justify-center gap-2 text-gray-700 text-sm'>
            <span>New to SPACEMAP?</span>
            <button
              type='button'
              onClick={() => navigate('/user/signup')}
              className='text-[#fccb16] hover:text-[#ffd84d] cursor-pointer font-medium'
            >
              Sign Up Now
            </button>
          </div>

          <div className='flex flex-col gap-3 mt-4'>
            {socialLoginButtons.map((item, index) => (
              <button
                key={index}
                type='button'
                onClick={() => handleSocialLogin(item.sns)}
                className='w-full h-12 bg-gray-100 text-gray-800 rounded-lg font-medium text-base cursor-pointer transition-all hover:bg-gray-200 border border-gray-300 flex items-center justify-center gap-2'
              >
                {item.sns === 'google' && (
                  <img
                    src='/icon/google.png'
                    alt='Google'
                    className='w-5 h-5'
                  />
                )}
                {/* {item.sns === 'linkedin' && (
                  <img
                    src='/icon/linkedin.png'
                    alt='LinkedIn'
                    className='w-5 h-5'
                  />
                )} */}
                {item.label}
              </button>
            ))}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
